import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api-client';

export interface AdminFundingAccount {
  id: string;
  tenantId: string;
  locationId: string;
  nickname: string;
  bankName: string;
  accountType: string;
  routingNumber: string;
  accountNumber: string;
  accountHolderName: string;
  accountBusinessType?: string | null;
  isPrimary: boolean;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface AdminFundingAccountsResponse {
  data: {
    send: AdminFundingAccount[] | null;
    receive: AdminFundingAccount[] | null;
  };
  errors?: { send?: string; receive?: string };
}

/**
 * Read-only list of funding accounts for a (tenant, location). Super-admin only.
 * Returns the send-side and receive-side independently; either may be null if
 * the corresponding upstream service is unreachable (see `errors`).
 */
export function useAdminFundingAccounts(tenantId: string, locationId?: string) {
  return useQuery({
    queryKey: ['admin', 'funding-accounts', tenantId, locationId],
    queryFn: async () =>
      api.get<AdminFundingAccountsResponse>(`/admin/tenants/${tenantId}/locations/${locationId}/funding-accounts`),
    enabled: !!tenantId && !!locationId,
  });
}

export type AdminFundingSide = 'send' | 'receive';

export interface AdminFundingAccountInput {
  nickname: string;
  bankName: string;
  accountType: 'checking' | 'savings';
  routingNumber: string;
  accountNumber: string;
  accountHolderName: string;
  accountBusinessType: 'business' | 'personal';
}

/**
 * Super-admin fallback: add a funding account for a location when the tenant
 * gave no (or wrong) bank details at onboarding. Created as primary by default.
 * Partial success is possible — `errors` names the side that failed.
 */
export function useAdminCreateFundingAccount(tenantId: string, locationId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: AdminFundingAccountInput & { sides: AdminFundingSide[] }) =>
      api.post<{
        data: Partial<Record<AdminFundingSide, AdminFundingAccount>>;
        errors?: Partial<Record<AdminFundingSide, string>>;
      }>(`/admin/tenants/${tenantId}/locations/${locationId}/funding-accounts`, body),
    onSettled: () => qc.invalidateQueries({ queryKey: ['admin', 'funding-accounts', tenantId, locationId] }),
  });
}

/** Super-admin fallback: correct a funding account's mismatched bank details. */
export function useAdminUpdateFundingAccount(tenantId: string, locationId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ side, id, ...body }: Partial<AdminFundingAccountInput> & { side: AdminFundingSide; id: string }) =>
      api.put<{ data: AdminFundingAccount }>(
        `/admin/tenants/${tenantId}/locations/${locationId}/funding-accounts/${side}/${id}`,
        body,
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'funding-accounts', tenantId, locationId] }),
  });
}

/** Super-admin: make this the location's primary account (the one Send Payments uses). */
export function useAdminSetPrimaryFundingAccount(tenantId: string, locationId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ side, id }: { side: AdminFundingSide; id: string }) =>
      api.put<{ data: AdminFundingAccount }>(
        `/admin/tenants/${tenantId}/locations/${locationId}/funding-accounts/${side}/${id}/set-primary`,
        {},
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'funding-accounts', tenantId, locationId] }),
  });
}
